/**
 @license
 Copyright (c) 2015-2026 Lablup Inc. All rights reserved.
 */
import { type ThemeConfig } from 'antd';
import * as _ from 'lodash-es';

export type LogoConfig = {
  src: string;
  srcCollapsed: string;
  srcDark?: string;
  srcCollapsedDark?: string;
  alt?: string;
  href?: string;
  size?: {
    width?: number;
    height?: number;
  };
  sizeCollapsed?: {
    width?: number;
    height?: number;
  };
  loginLogoSrc?: string;
  loginLogoSrcDark?: string;
  loginLogoSize?: {
    width?: number;
    height?: number;
  };
  aboutLogoSrc?: string;
  aboutLogoSrcDark?: string;
  aboutLogoSize?: {
    width?: number;
    height?: number;
  };
  /** @deprecated Use `aboutLogoSize` instead. */
  aboutModalSize?: {
    width?: number;
    height?: number;
  };
};
export type SiderConfig = {
  theme?: 'light' | 'dark';
};
export type BrandingConfig = {
  companyName?: string;
  brandName?: string;
};
export type ThemeFamilyConfig = {
  /** Ant Design theme config for the light scheme of this family. */
  light: ThemeConfig;
  /** Ant Design theme config for the dark scheme of this family. */
  dark: ThemeConfig;
  /** Human-readable label shown in the family selector. Falls back to the key. */
  label?: string;
};

export type CustomThemeConfig = {
  fontFamily?: string;
  light: ThemeConfig;
  dark: ThemeConfig;
  /**
   * Selectable theme families keyed by family id (e.g. `stained`, `glass`).
   * The `default` family is always synthesized from the top-level
   * `light`/`dark` above — a `default` key here is ignored — so pre-family
   * theme.json files (no `families` block) keep working as a single-entry
   * catalog.
   */
  families?: Record<string, ThemeFamilyConfig>;
  logo: LogoConfig;
  sider?: SiderConfig;
  branding?: BrandingConfig;
};

let _customTheme: CustomThemeConfig | undefined;

export const getCustomTheme = () => _customTheme;

/**
 * `useSyncExternalStore` subscription for the loaded theme. React re-reads
 * the snapshot right after subscribing, so a theme that settles between a
 * component's first render and its subscription is not missed.
 */
export const subscribeCustomTheme = (onChange: () => void) => {
  document.addEventListener('custom-theme-loaded', onChange);
  return () => document.removeEventListener('custom-theme-loaded', onChange);
};

/**
 * Keep only structurally valid family entries (each must carry both `light`
 * and `dark` theme configs) so a malformed `families` block in theme.json
 * degrades to fewer families instead of a broken catalog.
 */
export function pickValidThemeFamilies(
  input: unknown,
): Record<string, ThemeFamilyConfig> | undefined {
  if (!_.isPlainObject(input)) {
    return undefined;
  }
  return _.pickBy(
    input as Record<string, ThemeFamilyConfig>,
    (family) =>
      _.isPlainObject(family) &&
      _.isPlainObject(family.light) &&
      _.isPlainObject(family.dark),
  );
}

const GENERIC_FAMILIES = new Set([
  'serif',
  'sans-serif',
  'monospace',
  'cursive',
  'fantasy',
  'system-ui',
  'ui-serif',
  'ui-sans-serif',
  'ui-monospace',
  'ui-rounded',
  'emoji',
  'math',
  'fangsong',
]);

function parseFontFamilies(fontFamily: string): string[] {
  return fontFamily
    .split(',')
    .map((f) => f.trim().replace(/^['"]|['"]$/g, ''))
    .filter((f) => f.length > 0)
    .filter((f) => !GENERIC_FAMILIES.has(f.toLowerCase()))
    .filter((f) => !f.startsWith('-'));
}

function normalizeFontName(name: string): string {
  return name.toLowerCase().replace(/\s+/g, '-');
}

function injectFontCSS(fontFamilies: string[]) {
  const seen = new Set<string>();
  for (const family of fontFamilies) {
    const normalized = normalizeFontName(family);
    if (seen.has(normalized)) continue;
    seen.add(normalized);
    const href = `resources/fonts/${normalized}/${normalized}.css`;
    if (document.querySelector(`link[href="${href}"]`)) continue;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    document.head.appendChild(link);
  }
}

export const loadCustomThemeConfig = () => {
  fetch('resources/theme.json')
    .then((response) => response.json())
    .then((theme) => {
      if (_.isUndefined(theme.light)) {
        _customTheme = { light: theme, dark: theme, logo: theme.logo };
      } else {
        _customTheme = theme;
      }
      if (
        _customTheme &&
        import.meta.env.DEV &&
        import.meta.env.VITE_THEME_HEADER_COLOR
      ) {
        _.set(
          _customTheme,
          'light.components.Layout.headerBg',
          import.meta.env.VITE_THEME_HEADER_COLOR,
        );
        _.set(
          _customTheme,
          'dark.components.Layout.headerBg',
          import.meta.env.VITE_THEME_HEADER_COLOR,
        );
      }

      // Propagate top-level fontFamily into light/dark tokens so Ant Design receives it
      if (_customTheme) {
        const topLevelFont = _customTheme.fontFamily;
        if (topLevelFont) {
          _.set(_customTheme, 'light.token.fontFamily', topLevelFont);
          _.set(_customTheme, 'dark.token.fontFamily', topLevelFont);
        }

        // Auto-load font CSS based on fontFamily in theme config
        const fontFamily =
          topLevelFont ??
          (_.get(_customTheme, 'light.token.fontFamily') as
            | string
            | undefined) ??
          (_.get(_customTheme, 'dark.token.fontFamily') as string | undefined);
        if (fontFamily) {
          injectFontCSS(parseFontFamilies(fontFamily));
        }
      }

      document.dispatchEvent(new CustomEvent('custom-theme-loaded'));
    });
};
